import React, { useEffect, useMemo, useState } from 'react'
import { actions, actions as assignmentActions } from '../../../../store/reducers/assignment.reducer'
import { actions as compareSnippetsActions } from '../../../../store/reducers/compare-snippets.reducer'
import { connect, useDispatch, useSelector } from 'react-redux'
import { Checkbox, Collapse, Row } from 'antd'
import FileDrawer from '../../components/file-drawer/FileDrawer'
import { useParams } from 'react-router'
import UnitTestsFilters from './filters/UnitTestsFilters'
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle'
import ClusterGroup from '../../components/cluster/ClusterGroup'
import { ASSIGNMENT_TYPES } from '../../../../constants/assignment-types'
import PatternFilters from './filters/PatternFilters'
import EditPatternModal from '../../components/edit-pattern-modal/EditPatternModal'
import CreateSemanticPatternModal from '../../components/create-pattern-from-feedback-modal/CreateSemanticPatternModal'
import { checkIfArrayIncludesObjectProperty } from '../../../../utils/array.helper'
import { LANGUAGES } from '../../../../constants/languages'
import GroupsHeader from './header/GroupsHeader'
import patternSelectors from '../../../../store/selectors/pattern.selectors'
import ReviewFilters from './filters/ReviewFilters'
import userSelectors from '../../../../store/selectors/user.selectors'
import ActionsSection from './ActionsSection'
import assignmentSelectors from '../../../../store/selectors/assignment.selectors'
import { submissionActions } from '../../../../store/slices/submission.slice'
import submissionSelectors from '../../../../store/selectors/submission.selectors'
import StudentFeedbackFilters from './filters/StudentFeedbackFilters'
import { isMba } from '../../../../utils/assignment.helper'
import useDefaultClustersCollapse from '../../../../hooks/useDefaultClustersCollapse'
import { DEFAULT_COLLAPSE_THRESHOLD } from '../../../../constants/collapse-thresholds'

const ClustersSection = ({
    filters,
    showEditPatternModal,
    showCreateSemanticPatternModal,
    switchIncludeAnonSubmissionsFilter,
    setMissingPatternFeedbackFilter,
    setMissingGroupFeedbackFilter,
    setConfirmChangesMode,
    setIsFlakyFilter,
    fetchUnitTestResults,
}) => {
    const name = useSelector(assignmentSelectors.name)
    useDocumentTitle(`${name || ''} Groups`)
    const finetunedPatternExists = useSelector(patternSelectors.finetunedPatternExists)
    const isShopUser = useSelector(userSelectors.isShopUser)
    const isSenseUser = useSelector(userSelectors.isSenseUser)
    const language = useSelector(assignmentSelectors.language)
    const assignmentType = useSelector(assignmentSelectors.type)
    const showSubmissionDrawer = useSelector(submissionSelectors.showSubmissionDrawer)

    const dispatch = useDispatch()

    const { token, layerId } = useParams()

    const [activePanels, setActivePanels] = useState([])

    // Compute total files across visible clusters (after store filters applied upstream)
    const clusters = useSelector(state => state.assignment.clusters)
    const totalFilesCount = useMemo(
        () => clusters?.reduce((acc, c) => acc + (Array.isArray(c.files) ? c.files.length : 0), 0) || 0,
        [clusters],
    )
    useDefaultClustersCollapse(clusters, totalFilesCount, DEFAULT_COLLAPSE_THRESHOLD)

    useEffect(() => {
        fetchUnitTestResults(token)
    }, [layerId, token])

    useEffect(() => {
        return () => {
            dispatch(submissionActions.resetSelectedSubmissionIds())
            setConfirmChangesMode(false)
        }
    }, [])

    const handleMissingPatternFeedbackFilterChange = event => setMissingPatternFeedbackFilter(event.target.checked)

    const handleMissingGroupFeedbackFilterChange = event => setMissingGroupFeedbackFilter(event.target.checked)

    const handleIsFlakyChange = event => setIsFlakyFilter(event.target.checked)

    const handleCollapseChange = newActivePanels => setActivePanels(newActivePanels)

    const handleIncludeAnonSubmissionsChange = () => switchIncludeAnonSubmissionsFilter(token, layerId)

    const collapsePanels = [
        {
            key: 'patterns',
            label: 'Patterns',
            children: <PatternFilters />,
            hiddenfromlanguages: [LANGUAGES.EXCEL.value],
        },
        {
            key: 'unitTests',
            label: 'Unit Tests',
            children: <UnitTestsFilters />,
            hiddenfromlanguages: [LANGUAGES.EXCEL.value, LANGUAGES.TEXT.value],
        },
        ...(isShopUser
            ? []
            : [
                  {
                      key: 'feedback',
                      label: 'Feedback',
                      children: (
                          <>
                              {language === LANGUAGES.EXCEL.value ? null : (
                                  <Row>
                                      <Checkbox
                                          checked={filters.feedback.missingPatternFeedback}
                                          onChange={handleMissingPatternFeedbackFilterChange}
                                      >
                                          Missing Pattern Feedback
                                      </Checkbox>
                                  </Row>
                              )}
                              <Row>
                                  <Checkbox
                                      checked={filters.feedback.missingGroupFeedback}
                                      onChange={handleMissingGroupFeedbackFilterChange}
                                  >
                                      Missing Group Feedback
                                  </Checkbox>
                              </Row>
                          </>
                      ),
                  },
              ]),
        {
            key: 'type',
            label: 'Type',
            children: (
                <Row>
                    <Checkbox checked={filters.type.anonymousSubmissions} onChange={handleIncludeAnonSubmissionsChange}>
                        Include Anonymous Submissions
                    </Checkbox>
                </Row>
            ),
            hiddenfromassignmenttypes: [ASSIGNMENT_TYPES.MODEL.value, ASSIGNMENT_TYPES.ASSIGNMENT.value],
        },
        {
            key: 'properties',
            label: 'Properties',
            children: (
                <>
                    <Row>
                        <Checkbox checked={filters.properties.isFlaky} onChange={handleIsFlakyChange}>
                            Flaky
                        </Checkbox>
                    </Row>
                </>
            ),
        },
        ...(finetunedPatternExists
            ? [
                  {
                      key: 'review',
                      label: 'Review',
                      children: <ReviewFilters />,
                  },
              ]
            : []),
        ...(isSenseUser && isMba(assignmentType)
            ? [
                  {
                      key: 'student-feedback',
                      label: 'Student Feedback',
                      children: <StudentFeedbackFilters />,
                  },
              ]
            : []),
    ]

    const assignmentDetails = {}
    if (assignmentType) assignmentDetails.assignment_type = assignmentType
    if (language) assignmentDetails.language = language
    const filteredCollapsePanels = collapsePanels.filter(
        item =>
            checkIfArrayIncludesObjectProperty(item.hiddenfromassignmenttypes, assignmentDetails, 'assignment_type') &&
            checkIfArrayIncludesObjectProperty(item.hiddenfromlanguages, assignmentDetails, 'language'),
    )

    return (
        <>
            <div className='clusters-sidebar'>
                <ActionsSection />
                <Collapse
                    ghost
                    bordered={false}
                    onChange={handleCollapseChange}
                    activeKey={activePanels}
                    items={filteredCollapsePanels}
                />
            </div>
            <div className='clusters-container'>
                <GroupsHeader />
                <ClusterGroup />
            </div>
            {showSubmissionDrawer && <FileDrawer />}
            {showEditPatternModal && <EditPatternModal />}
            {showCreateSemanticPatternModal && <CreateSemanticPatternModal />}
        </>
    )
}

const mapState = ({ assignment }) => {
    return {
        clusters: assignment.clusters,
        filters: assignment.filters,
        showEditPatternModal: assignment.showEditPatternModal,
        showCreateSemanticPatternModal: assignment.showCreateSemanticPatternModal,
    }
}

const mapDispatch = {
    setMissingPatternFeedbackFilter: (value) => assignmentActions.setMissingPatternFeedbackFilter(value),
    setMissingGroupFeedbackFilter: (value) => assignmentActions.setMissingGroupFeedbackFilter(value),
    setShowCompareSnippetsModal: (value) => compareSnippetsActions.setShowModal(value),
    switchIncludeAnonSubmissionsFilter: (token, layerId) => assignmentActions.switchIncludeAnonSubmissionsFilter(token, layerId),
    setConfirmChangesMode: (value) => assignmentActions.setConfirmChangesMode(value),
    setIsFlakyFilter: (value) => actions.setIsFlakyFilter(value),
    setShowMoveSelectedFilesToAnotherGroupModal: (value) => actions.setShowMoveSelectedFilesToAnotherGroupModal(value),
    setFirstSnippet: (value) => compareSnippetsActions.setFirstSnippet(value),
    setSecondSnippet: (value) => compareSnippetsActions.setSecondSnippet(value),
    selectSubgroupWithSamePatterns: (token, layerId) => actions.selectSubgroupWithSamePatterns(token, layerId),
    isExcelAssignment: () => actions.isExcelAssignment(),
    fetchUnitTestResults: (token) => actions.fetchUnitTestResults(token),
}

export default connect(mapState, mapDispatch)(ClustersSection)
